import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Timeline,
  Typography,
} from "antd";
import React, { useState } from "react";
import { CustomTextField } from "~/components";
import { FormattedLeaseData } from "~/types/lease";
import { COLORS } from "~/utils";
import * as APITypes from "~/API";
import { useFetcher } from "react-router-dom";
import dayjs from "dayjs";
import { useAppTranslation } from "~/hooks/useAppTranslation";

type _DetailsType =
  | "application"
  | "background"
  | "offer"
  | "moveIn"
  | "moveOut"
  | "none";

const getTimelineDot = (
  status?:
    | APITypes.ApplicationStatusType
    | APITypes.BackgroundCheckStatusType
    | APITypes.OfferStatusType
    | APITypes.MoveExamStatusType
): { dot: any; color: string } => {
  switch (status) {
    case "UnderReview":
    case "Submitted":
    case "InProgress":
      return { dot: <ClockCircleOutlined />, color: COLORS.blue };
    case "Approved":
    case "Completed":
      return { dot: <CheckCircleOutlined />, color: COLORS.vibrant_green };
    case "Rejected":
    case "Failed":
      return { dot: <CloseCircleOutlined />, color: COLORS.red };
    case "Pending":
      return { dot: <ClockCircleOutlined />, color: COLORS.grey };
  }

  return { dot: <ClockCircleOutlined />, color: COLORS.blue };
};

const LeaseHistoryItem = ({
  leaseField,
  detailsType,
  itemDescription,
  showDetails,
  hoverDetails,
  setShowDetails,
  setHoverDetails,
}: {
  leaseField?:
    | APITypes.Application
    | APITypes.BackgroundCheck
    | APITypes.Offer
    | APITypes.MoveExam;
  detailsType: _DetailsType;
  itemDescription: string;
  showDetails: _DetailsType;
  hoverDetails: _DetailsType;
  setShowDetails: any;
  setHoverDetails: any;
}) => {
  const { t } = useAppTranslation();
  return (
    <Space direction="vertical">
      <Typography.Text
        onClick={() => setShowDetails(detailsType)}
        style={{
          cursor: "pointer",
          backgroundColor:
            hoverDetails === detailsType ? COLORS.light_blue : "transparent",
          color: showDetails === detailsType ? COLORS.pastel_mint : "black",
          transition: "background-color 0.3s ease",
        }}
        onMouseEnter={() => setHoverDetails(detailsType)}
        onMouseLeave={() => setHoverDetails("none")}
      >
        {t(itemDescription)}: {leaseField?.status}{" "}
      </Typography.Text>
      <Typography.Text type="secondary" italic>
        {t("Start_Date")}: {leaseField?.startDate}
        {leaseField?.status === "InProgress" ||
        leaseField?.status === APITypes.OfferStatusType.LandlordComplete ||
        leaseField?.status === APITypes.OfferStatusType.TenantComplete ||
        leaseField?.status === "UnderReview" ? (
          ""
        ) : (
          <>
            , {t("End_Date")}: {leaseField?.endDate}
          </>
        )}
      </Typography.Text>
    </Space>
  );
};

export const LeaseHistory = ({
  lease,
  disable,
}: {
  lease: FormattedLeaseData;
  disable: boolean;
}) => {
  const [showDetails, setShowDetails] = React.useState<_DetailsType>("none");
  const [hoverDetails, setHoverDetails] = React.useState<_DetailsType>("none");

  return (
    <Row>
      <Col xs={24} lg={8} style={{ paddingRight: 20 }}>
        <Card
          bordered={true}
          style={{
            maxWidth: "700px",
            margin: "0",
            textAlign: "left",
            border: "2px solid #1890ff",
            borderRadius: "8px",
          }}
        >
          <Timeline
            items={[
              {
                key: "application",
                dot: getTimelineDot(lease.application?.status).dot,
                color: getTimelineDot(lease.application?.status).color,
                children: LeaseHistoryItem({
                  leaseField: lease.application,
                  detailsType: "application",
                  itemDescription: "Application_Info",
                  showDetails,
                  hoverDetails,
                  setShowDetails: setShowDetails,
                  setHoverDetails: setHoverDetails,
                }),
              },
              {
                key: "background",
                dot: getTimelineDot(lease.background?.status).dot,
                color: getTimelineDot(lease.background?.status).color,
                children: LeaseHistoryItem({
                  leaseField: lease.background,
                  detailsType: "background",
                  itemDescription: "Background_Check_Info",
                  showDetails,
                  hoverDetails,
                  setShowDetails: setShowDetails,
                  setHoverDetails: setHoverDetails,
                }),
              },
              {
                key: "offer",
                dot: getTimelineDot(lease.offer?.status).dot,
                color: getTimelineDot(lease.offer?.status).color,
                children: LeaseHistoryItem({
                  leaseField: lease.offer,
                  detailsType: "offer",
                  itemDescription: "Offer_Info",
                  showDetails,
                  hoverDetails,
                  setShowDetails: setShowDetails,
                  setHoverDetails: setHoverDetails,
                }),
              },
              {
                key: "moveIn",
                dot: getTimelineDot(lease.moveIn?.status).dot,
                color: getTimelineDot(lease.moveIn?.status).color,
                children: LeaseHistoryItem({
                  leaseField: lease.moveIn,
                  detailsType: "moveIn",
                  itemDescription: "Move_In_Info",
                  showDetails,
                  hoverDetails,
                  setShowDetails: setShowDetails,
                  setHoverDetails: setHoverDetails,
                }),
              },
              {
                key: "moveOut",
                dot: getTimelineDot(lease.moveOut?.status).dot,
                color: getTimelineDot(lease.moveOut?.status).color,
                children: LeaseHistoryItem({
                  leaseField: lease.moveOut,
                  detailsType: "moveOut",
                  itemDescription: "Move_Out_Info",
                  showDetails,
                  hoverDetails,
                  setShowDetails: setShowDetails,
                  setHoverDetails: setHoverDetails,
                }),
              },
            ]}
          />
        </Card>
      </Col>

      <Col xs={24} lg={10}>
        {showDetails === "application" && <Application lease={lease} />}

        {showDetails === "background" && <BackgroundCheck lease={lease} />}

        {showDetails === "offer" && <Offer lease={lease} />}

        {showDetails === "moveIn" && <MoveIn lease={lease} />}

        {showDetails === "moveOut" && <MoveOut lease={lease} />}
      </Col>
    </Row>
  );
};

const Application = ({ lease }: { lease: FormattedLeaseData }) => {
  const { t } = useAppTranslation();
  return (
    <>
      <Typography.Title>{t("Application_Info")}</Typography.Title>

      <Row gutter={12} style={{ marginTop: "2rem" }}>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Start_Date")}
            </Typography.Text>
            <CustomTextField value={lease.application?.startDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("End_Date")}
            </Typography.Text>
            <CustomTextField value={lease.application?.endDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Status")}
            </Typography.Text>
            <CustomTextField value={lease.application?.status || ""} />
          </div>
        </Col>
      </Row>
    </>
  );
};

const BackgroundCheck = ({ lease }: { lease: FormattedLeaseData }) => {
  const { t } = useAppTranslation();
  return (
    <>
      <Typography.Title>{t("Background_Check_Info")}</Typography.Title>

      <Row gutter={12} style={{ marginTop: "2rem" }}>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Start_Date")}
            </Typography.Text>
            <CustomTextField value={lease.background?.startDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("End_Date")}
            </Typography.Text>
            <CustomTextField value={lease.background?.endDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Status")}
            </Typography.Text>
            <CustomTextField value={lease.background?.status || ""} />
          </div>
        </Col>
      </Row>
    </>
  );
};

const Offer = ({ lease }: { lease: FormattedLeaseData }) => {
  const { t } = useAppTranslation();
  const [loading, setLoading] = useState(false);
  const fetcher = useFetcher();
  const [form] = Form.useForm();
  const date = dayjs();

  const handleLeaseApplicationSignature = async (values: any) => {
    setLoading(true);

    fetcher.submit(
      {
        body: JSON.stringify(values),
      },
      { method: "post" }
    );

    form.resetFields();
    setLoading(false);
  };

  return (
    <>
      <Typography.Title>{t("Offer_Info")}</Typography.Title>

      <Row gutter={12} style={{ marginTop: "2rem" }}>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Start_Date")}
            </Typography.Text>
            <CustomTextField value={lease.offer?.startDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("End_Date")}
            </Typography.Text>
            <CustomTextField value={lease.offer?.endDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Status")}
            </Typography.Text>
            <CustomTextField value={lease.offer?.status || ""} />
          </div>
        </Col>
      </Row>

      <Row gutter={12} style={{ marginTop: "2rem" }}>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("tenant_sign_date")}
            </Typography.Text>
            <CustomTextField
              value={lease.offer?.tenantSignDate || ""}
              prefix={<CalendarOutlined />}
            />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("landlord_sign_date")}
            </Typography.Text>
            <CustomTextField
              value={lease.offer?.landlordSignDate || ""}
              prefix={<CalendarOutlined />}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={12} style={{ marginTop: "2rem" }}>
        <Col span={8}>
          <Button
            onClick={() =>
              alert("TODO: this will allow tenants to see lease agreement")
            }
          >
            {t("View_Lease_Agreement")}
          </Button>
        </Col>
        {(lease.offer?.status === APITypes.OfferStatusType.InProgress ||
          lease.offer?.status ===
            APITypes.OfferStatusType.LandlordComplete) && (
          <Col span={16}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography.Text strong style={{ marginBottom: 5 }}>
                {t("Sign_Lease_Agreement")}
              </Typography.Text>
              <Form form={form} onFinish={handleLeaseApplicationSignature}>
                <Form.Item
                  className="lease-agreement-form-item"
                  label={t("Full_Name")}
                  name="fullname"
                  rules={[
                    {
                      required: true,
                      message: t("Please_enter_your_full_name"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  className="lease-agreement-form-item"
                  label={t("Date")}
                  name="date"
                >
                  <Input
                    defaultValue={`${
                      date.month() + 1
                    }/${date.date()}/${date.year()}`}
                    disabled={true}
                  />
                </Form.Item>
                <Form.Item className="lease-agreement-submit-button">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                  >
                    {t("Sign_Lease_Agreement")}
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </Col>
        )}
      </Row>
    </>
  );
};

const MoveIn = ({ lease }: { lease: FormattedLeaseData }) => {
  const { t } = useAppTranslation();
  return (
    <>
      <Typography.Title>{t("Move_In_Info")}</Typography.Title>

      <Row gutter={12} style={{ marginTop: "2rem" }}>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Start_Date")}
            </Typography.Text>
            <CustomTextField value={lease.moveIn?.startDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("End_Date")}
            </Typography.Text>
            <CustomTextField value={lease.moveIn?.endDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Status")}
            </Typography.Text>
            <CustomTextField value={lease.moveIn?.status || ""} />
          </div>
        </Col>
      </Row>
    </>
  );
};

const MoveOut = ({ lease }: { lease: FormattedLeaseData }) => {
  const { t } = useAppTranslation();
  return (
    <>
      <Typography.Title>{t("Move_Out_Info")}</Typography.Title>

      <Row gutter={12} style={{ marginTop: "2rem" }}>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Start_Date")}
            </Typography.Text>
            <CustomTextField value={lease.moveOut?.startDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("End_Date")}
            </Typography.Text>
            <CustomTextField value={lease.moveOut?.endDate || ""} />
          </div>
        </Col>
        <Col span={8}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Typography.Text strong style={{ marginBottom: 5 }}>
              {t("Status")}
            </Typography.Text>
            <CustomTextField value={lease.moveOut?.status || ""} />
          </div>
        </Col>
      </Row>
    </>
  );
};
